import { FunctionComponent, useState } from "react";
import { Tab, Tabs } from "react-bootstrap";
import { PaginatedGrid } from "../../../../../components/Grid";
import Title from "../../../../../components/Title";
import { AndroidBehavioral } from "../../../@types/rldynamicanalysis/android_behavioral";
import { DYNAMIC_ANALYSIS_EMPTY_MESSAGES } from "../consts";

const EMPTY_CELL_CONTENT = "- - -";

interface RlCloudSandboxAndroidBehavioralProps {
  data?: AndroidBehavioral;
  reportTitle: string;
}

const PERMISSIONS_SCHEMA = {
  fields: [
    {
      name: "permission",
      displayTitle: "Permission",
      component: (item: { permission: string; executed: boolean }) =>
        item.permission || EMPTY_CELL_CONTENT
    },
    {
      name: "executed",
      displayTitle: "Executed",
      component: (item: { permission: string; executed: boolean }) =>
        item.executed ? "Yes" : "No"
    }
  ]
};

const SERVICES_SCHEMA = {
  fields: [
    {
      name: "name",
      displayTitle: "Service Name",
      component: (item: { name: string }) => item.name || EMPTY_CELL_CONTENT
    }
  ]
};

const RECEIVERS_SCHEMA = {
  fields: [
    {
      name: "name",
      displayTitle: "Receiver Name",
      component: (item: { name: string }) => item.name || EMPTY_CELL_CONTENT
    },
    {
      name: "intents",
      displayTitle: "Intents",
      component: (item: { intents: string[] }) =>
        item.intents?.join(", ") || EMPTY_CELL_CONTENT,
      isArrayField: false
    }
  ]
};

const RlCloudSandboxAndroidBehavioral: FunctionComponent<
  RlCloudSandboxAndroidBehavioralProps
> = ({ data, reportTitle }) => {
  const [pageNum, setPageNum] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);

  const permissions = data?.permissions || [];
  const services = data?.dynamic_services || [];
  const receivers = data?.dynamic_receivers || [];

  const pageData = permissions.slice(
    (pageNum - 1) * currentPageSize,
    pageNum * currentPageSize
  );

  return (
    <div>
      <Title Level="h2" text={`Android Behavioral (${reportTitle})`} />

      {permissions.length || services.length || receivers.length ? (
        <Tabs
          id="rl-android-behavioral-tabs"
          variant="tabs"
          transition={false}
          defaultActiveKey="Permissions"
          className="rlbs"
        >
          <Tab
            eventKey="Permissions"
            title={`Permissions (${permissions.length})`}
            mountOnEnter
          >
            {permissions.length ? (
              <PaginatedGrid
                data={pageData}
                numPages={Math.ceil(permissions.length / currentPageSize)}
                schema={PERMISSIONS_SCHEMA}
                page={pageNum}
                pageSize={currentPageSize}
                totalResults={permissions.length}
                showPageNumbers
                onPageChange={(value: number, pageSize: number) => {
                  setPageNum(value);
                  setCurrentPageSize(pageSize);
                }}
              />
            ) : (
              <b>
                {
                  DYNAMIC_ANALYSIS_EMPTY_MESSAGES.android_behavioral
                    .no_permissions
                }
              </b>
            )}
          </Tab>
          <Tab
            eventKey="Services"
            title={`Dynamic Services (${services.length})`}
            mountOnEnter
          >
            {services.length ? (
              <PaginatedGrid
                data={services}
                numPages={Math.ceil(services.length / currentPageSize) || 1}
                schema={SERVICES_SCHEMA}
                page={pageNum}
                pageSize={currentPageSize}
                totalResults={services.length}
                showPageNumbers
                onPageChange={(value: number, pageSize: number) => {
                  setPageNum(value);
                  setCurrentPageSize(pageSize);
                }}
              />
            ) : (
              <b>
                {DYNAMIC_ANALYSIS_EMPTY_MESSAGES.android_behavioral.no_services}
              </b>
            )}
          </Tab>
          <Tab
            eventKey="Receivers"
            title={`Dynamic Receivers (${receivers.length})`}
            mountOnEnter
          >
            {receivers.length ? (
              <PaginatedGrid
                data={receivers}
                numPages={Math.ceil(receivers.length / currentPageSize) || 1}
                schema={RECEIVERS_SCHEMA}
                page={pageNum}
                pageSize={currentPageSize}
                totalResults={receivers.length}
                showPageNumbers
                onPageChange={(value: number, pageSize: number) => {
                  setPageNum(value);
                  setCurrentPageSize(pageSize);
                }}
              />
            ) : (
              <b>
                {
                  DYNAMIC_ANALYSIS_EMPTY_MESSAGES.android_behavioral
                    .no_receivers
                }
              </b>
            )}
          </Tab>
        </Tabs>
      ) : (
        <b>{DYNAMIC_ANALYSIS_EMPTY_MESSAGES.android_behavioral.no_data}</b>
      )}
    </div>
  );
};

export default RlCloudSandboxAndroidBehavioral;
